import { getUserRoles } from './authRoles';

const EMPTY_SCOPE_MAP = {
  app: [],
  consultant: [],
  network: [],
  organization: [],
};

const ROLE_GROUPS = {
  app_admin: { scope: 'app', aliases: ['hke_admin'] },
  consultant_advisor: { scope: 'consultant', aliases: ['consultant_advisor'] },
  network_admin: { scope: 'network', aliases: ['network_manager', 'network_admin'] },
  network_viewer: { scope: 'network', aliases: ['network_viewer', 'network_user', 'network_member'] },
  organization_admin: { scope: 'organization', aliases: ['org_master', 'organization_admin', 'org_admin'] },
  organization_user: { scope: 'organization', aliases: ['org_user', 'organization_user'] },
};

const normalizeRoleName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
const normalizeScope = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const toPositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const uniqueNumbers = (values) => Array.from(new Set(values.map(toPositiveNumber).filter(Boolean)));
const normalizeNameForSort = (value) => (typeof value === 'string' ? value.trim().toLocaleLowerCase() : '');

const extractIds = (source, keys = []) =>
  uniqueNumbers(
    keys.flatMap((key) => {
      const value = source?.[key];
      if (Array.isArray(value)) return value;
      return [value];
    })
  );

const normalizeAssignment = (assignment) => {
  if (!assignment || typeof assignment !== 'object') return null;

  const name = normalizeRoleName(
    assignment.role_name ??
      assignment.role?.name ??
      assignment.name ??
      assignment.role ??
      assignment.value
  );
  const scope = normalizeScope(assignment.scope ?? assignment.scope_type ?? assignment.role?.scope);

  if (!name) return null;

  return {
    name,
    scope,
    consultant_id: toPositiveNumber(
      assignment.consultant_id ??
        assignment.consultant?.id ??
        assignment.scope_id
    ),
    network_id: toPositiveNumber(
      assignment.network_id ??
        assignment.network?.id ??
        assignment.scope_id
    ),
    organization_id: toPositiveNumber(
      assignment.organization_id ??
        assignment.organization?.id ??
        assignment.org_id ??
        assignment.org?.id ??
        assignment.scope_id
    ),
  };
};

const getAssignments = (user) => {
  const directAssignments = Array.isArray(user?.rbac?.assignments) ? user.rbac.assignments : [];
  const roleEntries = []
    .concat(Array.isArray(user?.roles) ? user.roles : [])
    .concat(Array.isArray(user?.user_roles) ? user.user_roles : []);

  return directAssignments
    .concat(roleEntries)
    .map((entry) => (typeof entry === 'string' ? { name: normalizeRoleName(entry), scope: '' } : normalizeAssignment(entry)))
    .filter(Boolean);
};

const getRawRoleEntries = (user) =>
  []
    .concat(Array.isArray(user?.rbac?.assignments) ? user.rbac.assignments : [])
    .concat(Array.isArray(user?.roles) ? user.roles : [])
    .concat(Array.isArray(user?.user_roles) ? user.user_roles : [])
    .filter((entry) => entry && typeof entry === 'object');

const getNetworkMemberships = (user) =>
  Array.isArray(user?.network_memberships) ? user.network_memberships : [];

const getOrganizationMemberships = (user) =>
  Array.isArray(user?.organization_memberships) ? user.organization_memberships : [];

const hasIntersection = (values = [], allowedValues = []) => {
  if (!Array.isArray(values) || !Array.isArray(allowedValues) || values.length === 0 || allowedValues.length === 0) {
    return false;
  }

  const allowed = new Set(uniqueNumbers(allowedValues));
  return uniqueNumbers(values).some((value) => allowed.has(value));
};

const getScopedEntityIds = (user) => {
  const assignments = getAssignments(user);
  const rawEntries = getRawRoleEntries(user);

  const organizationIds = uniqueNumbers([
    user?.organization_id,
    user?.organization?.id,
    ...extractIds(user, ['organization_ids', 'manageable_organization_ids', 'managed_organization_ids']),
    ...assignments.map((assignment) => assignment.organization_id),
    ...getOrganizationMemberships(user).flatMap((membership) => [
      membership?.organization_id,
      membership?.organization?.id,
      membership?.org_id,
      membership?.org?.id,
    ]),
  ]);

  const networkIds = uniqueNumbers([
    user?.network_id,
    user?.network?.id,
    ...extractIds(user, ['network_ids', 'manageable_network_ids', 'managed_network_ids']),
    ...assignments.map((assignment) => assignment.network_id),
    ...getNetworkMemberships(user).flatMap((membership) => [
      membership?.network_id,
      membership?.network?.id,
    ]),
    ...getOrganizationMemberships(user).flatMap((membership) => [
      membership?.network_id,
      membership?.network?.id,
      membership?.organization?.network_id,
      membership?.organization?.network?.id,
    ]),
    ...rawEntries.flatMap((entry) => [
      entry?.organization?.network_id,
      entry?.organization?.network?.id,
      ...(Array.isArray(entry?.organization?.network_ids) ? entry.organization.network_ids : []),
    ]),
  ]);

  const consultantIds = uniqueNumbers([
    user?.consultant_id,
    user?.consultant?.id,
    ...extractIds(user, ['consultant_ids', 'managed_consultant_ids']),
    ...assignments.map((assignment) => assignment.consultant_id),
    ...rawEntries.flatMap((entry) => [
      entry?.network?.consultant_id,
      entry?.network?.consultant?.id,
      entry?.organization?.consultant_id,
      entry?.organization?.consultant?.id,
    ]),
  ]);

  return {
    consultantIds,
    networkIds,
    organizationIds,
  };
};

const aliasesFor = (groupKey) => ROLE_GROUPS[groupKey]?.aliases || [];

const resolveRoleNameFromCatalog = (groupKey, availableRolesByScope = EMPTY_SCOPE_MAP) => {
  const group = ROLE_GROUPS[groupKey];
  if (!group) return null;

  const catalogRoles = Array.isArray(availableRolesByScope[group.scope]) ? availableRolesByScope[group.scope] : [];
  const matchedRole = group.aliases.find((alias) => catalogRoles.includes(alias));
  return matchedRole || group.aliases[0] || null;
};

export const getUserCreationPolicy = (user) => {
  const roles = getUserRoles(user);
  const assignments = getAssignments(user);

  const consultantIds = uniqueNumbers([
    user?.consultant_id,
    user?.consultant?.id,
    ...extractIds(user, ['consultant_ids', 'managed_consultant_ids']),
    ...assignments.flatMap((assignment) =>
      aliasesFor('consultant_advisor').includes(assignment.name) || roles.includes('consultant_admin')
        ? [assignment.consultant_id]
        : []
    ),
  ]);

  const managedNetworkIds = uniqueNumbers([
    user?.network_id,
    user?.network?.id,
    ...extractIds(user, ['network_ids', 'managed_network_ids', 'manageable_network_ids']),
    ...getNetworkMemberships(user).flatMap((membership) =>
      ['network_admin', 'network_manager'].includes(normalizeRoleName(membership?.role_name ?? membership?.role?.name ?? membership?.role))
        ? [membership?.network_id ?? membership?.network?.id]
        : []
    ),
    ...assignments.flatMap((assignment) =>
      ['network_admin', 'network_manager', 'network_viewer', 'network_user', 'network_member'].includes(assignment.name)
        ? [assignment.network_id]
        : []
    ),
  ]);

  const managedOrganizationIds = uniqueNumbers([
    user?.organization_id,
    user?.organization?.id,
    ...extractIds(user, ['organization_ids', 'managed_organization_ids', 'manageable_organization_ids']),
    ...assignments.flatMap((assignment) =>
      ['organization_admin', 'org_admin', 'org_master', 'org_user', 'organization_user'].includes(assignment.name)
        ? [assignment.organization_id]
        : []
    ),
  ]);

  const isHkeAdmin = roles.includes('hke_admin');
  const isConsultantAdmin = roles.includes('consultant_admin');
  const isNetworkManager = roles.includes('network_manager') || roles.includes('network_admin');
  const isOrganizationAdmin =
    roles.includes('organization_admin') || roles.includes('org_admin') || roles.includes('org_master');

  const allowedRoleGroups = [];

  if (isHkeAdmin) {
    allowedRoleGroups.push(
      'app_admin',
      'consultant_advisor',
      'network_admin',
      'network_viewer',
      'organization_admin',
      'organization_user'
    );
  } else if (isConsultantAdmin) {
    allowedRoleGroups.push(
      'consultant_advisor',
      'network_admin',
      'network_viewer',
      'organization_admin',
      'organization_user'
    );
  } else if (isNetworkManager) {
    allowedRoleGroups.push('network_viewer', 'organization_admin', 'organization_user');
  } else if (isOrganizationAdmin) {
    allowedRoleGroups.push('organization_user');
  }

  const allowedRoleGroupsByScope = allowedRoleGroups.reduce((accumulator, groupKey) => {
    const group = ROLE_GROUPS[groupKey];
    if (!group) return accumulator;
    accumulator[group.scope] = [...(accumulator[group.scope] || []), groupKey];
    return accumulator;
  }, { ...EMPTY_SCOPE_MAP });

  return {
    canCreateUsers: allowedRoleGroups.length > 0,
    isHkeAdmin,
    isConsultantAdmin,
    isNetworkManager,
    isOrganizationAdmin,
    consultantIds,
    managedNetworkIds,
    managedOrganizationIds,
    allowedRoleGroups,
    allowedRoleGroupsByScope,
  };
};

const organizationBelongsToNetwork = (organization, networkIds = []) => {
  const normalizedNetworkIds = new Set(uniqueNumbers(networkIds));
  if (normalizedNetworkIds.size === 0) return false;

  const candidateIds = uniqueNumbers([
    organization?.network_id,
    organization?.network?.id,
    ...(Array.isArray(organization?.network_ids) ? organization.network_ids : []),
    ...(Array.isArray(organization?.networks) ? organization.networks.map((network) => network?.id) : []),
  ]);

  return candidateIds.some((networkId) => normalizedNetworkIds.has(networkId));
};

const sortOrganizationsByName = (organizations = []) =>
  organizations
    .map((organization, index) => ({
      organization,
      index,
      sortName: normalizeNameForSort(organization?.name),
      hasName: normalizeNameForSort(organization?.name) !== '',
    }))
    .sort((left, right) => {
      if (left.hasName !== right.hasName) return left.hasName ? -1 : 1;
      if (left.sortName < right.sortName) return -1;
      if (left.sortName > right.sortName) return 1;
      return left.index - right.index;
    })
    .map(({ organization }) => organization);

export const filterNetworksForPolicy = (networks = [], policy, context = { type: 'hke' }) => {
  if (!Array.isArray(networks)) return [];
  if (policy?.isHkeAdmin) return networks;

  const contextId = toPositiveNumber(context?.id);
  if (context?.type === 'network' && contextId) {
    return networks.filter((network) => toPositiveNumber(network?.id) === contextId);
  }

  if (policy?.managedNetworkIds?.length > 0) {
    const allowedIds = new Set(policy.managedNetworkIds);
    return networks.filter((network) => allowedIds.has(toPositiveNumber(network?.id)));
  }

  if (policy?.consultantIds?.length > 0) {
    const allowedConsultantIds = new Set(policy.consultantIds);
    return networks.filter((network) => allowedConsultantIds.has(toPositiveNumber(network?.consultant_id)));
  }

  return [];
};

export const filterOrganizationsForPolicy = (organizations = [], policy, context = { type: 'hke' }) => {
  if (!Array.isArray(organizations)) return [];
  if (policy?.isHkeAdmin) {
    if (context?.type === 'organization' && toPositiveNumber(context?.id)) {
      return sortOrganizationsByName(
        organizations.filter((organization) => toPositiveNumber(organization?.id) === toPositiveNumber(context.id))
      );
    }
    if (context?.type === 'network' && toPositiveNumber(context?.id)) {
      return sortOrganizationsByName(
        organizations.filter((organization) => organizationBelongsToNetwork(organization, [context.id]))
      );
    }
    return sortOrganizationsByName(organizations);
  }

  const contextId = toPositiveNumber(context?.id);
  if (context?.type === 'organization' && contextId) {
    return sortOrganizationsByName(
      organizations.filter((organization) => toPositiveNumber(organization?.id) === contextId)
    );
  }

  if (policy?.managedOrganizationIds?.length > 0) {
    const allowedIds = new Set(policy.managedOrganizationIds);
    return sortOrganizationsByName(
      organizations.filter((organization) => allowedIds.has(toPositiveNumber(organization?.id)))
    );
  }

  const networkIds =
    context?.type === 'network' && contextId
      ? [contextId]
      : policy?.managedNetworkIds || [];

  if (networkIds.length > 0) {
    return sortOrganizationsByName(
      organizations.filter((organization) => organizationBelongsToNetwork(organization, networkIds))
    );
  }

  if (policy?.consultantIds?.length > 0) {
    const allowedConsultantIds = new Set(policy.consultantIds);
    return sortOrganizationsByName(
      organizations.filter((organization) => allowedConsultantIds.has(toPositiveNumber(organization?.consultant_id)))
    );
  }

  return [];
};

export const filterConsultantsForPolicy = (consultants = [], policy) => {
  if (!Array.isArray(consultants)) return [];
  if (policy?.isHkeAdmin) return consultants;

  if (policy?.consultantIds?.length > 0) {
    const allowedIds = new Set(policy.consultantIds);
    return consultants.filter((consultant) => allowedIds.has(toPositiveNumber(consultant?.id)));
  }

  return [];
};

export const resolveAllowedScopes = (policy, context = { type: 'hke' }) => {
  const scopes = Object.entries(policy?.allowedRoleGroupsByScope || EMPTY_SCOPE_MAP)
    .filter(([, groups]) => groups.length > 0)
    .map(([scope]) => scope);

  if (context?.type === 'network') {
    return scopes.filter((scope) => scope === 'network' || scope === 'organization');
  }

  if (context?.type === 'organization') {
    return scopes.filter((scope) => scope === 'organization');
  }

  if (context?.type === 'consultant') {
    return scopes.filter((scope) => scope !== 'app');
  }

  return scopes;
};

export const resolveAllowedRolesByScope = (policy, availableRolesByScope = EMPTY_SCOPE_MAP, context = { type: 'hke' }) => {
  const allowedScopes = new Set(resolveAllowedScopes(policy, context));

  return Object.entries(policy?.allowedRoleGroupsByScope || EMPTY_SCOPE_MAP).reduce(
    (accumulator, [scope, groupKeys]) => {
      if (!allowedScopes.has(scope)) {
        accumulator[scope] = [];
        return accumulator;
      }

      accumulator[scope] = groupKeys
        .map((groupKey) => resolveRoleNameFromCatalog(groupKey, availableRolesByScope))
        .filter(Boolean);
      return accumulator;
    },
    { ...EMPTY_SCOPE_MAP }
  );
};

export const canCreateUsersInContext = (user, context = { type: 'hke' }) => {
  const policy = getUserCreationPolicy(user);
  if (!policy.canCreateUsers) return false;

  const contextId = toPositiveNumber(context?.id);
  switch (context?.type) {
    case 'network':
      if (!contextId) return false;
      if (policy.isHkeAdmin || policy.isConsultantAdmin) return true;
      return policy.managedNetworkIds.includes(contextId);
    case 'organization':
      if (!contextId) return false;
      if (policy.isHkeAdmin || policy.isConsultantAdmin) return true;
      if (policy.managedOrganizationIds.includes(contextId)) return true;
      return policy.managedNetworkIds.length > 0;
    case 'consultant':
      return policy.isHkeAdmin || (policy.isConsultantAdmin && policy.consultantIds.length > 0);
    case 'hke':
    default:
      return policy.canCreateUsers;
  }
};

export const canManageUsersForOrganization = (user, organizationId) => {
  const id = toPositiveNumber(organizationId);
  if (!id) return false;

  const policy = getUserCreationPolicy(user);
  if (!policy.canCreateUsers) return false;
  if (policy.isHkeAdmin || policy.isConsultantAdmin) return true;
  return policy.managedOrganizationIds.includes(id);
};

export const canSimulateUser = (currentUser, targetUser) => {
  if (!currentUser || !targetUser || currentUser.id === targetUser.id) return false;

  const currentUserRoles = getUserRoles(currentUser);
  if (!currentUserRoles.includes('hke_admin')) return false;

  const targetRoles = getUserRoles(targetUser);
  const targetRole = normalizeRoleName(targetUser?.role);
  return targetRole === 'org_user' || targetRoles.includes('org_user') || targetRoles.includes('organization_user');
};

export const canAccessUserInContext = (currentUser, targetUser, context = { type: 'hke' }) => {
  if (!targetUser) return false;

  const policy = getUserCreationPolicy(currentUser);
  if (policy.isHkeAdmin) return true;

  const scopedIds = getScopedEntityIds(targetUser);
  const contextId = toPositiveNumber(context?.id);

  if (context?.type === 'organization' && contextId) {
    return scopedIds.organizationIds.includes(contextId);
  }

  if (context?.type === 'network' && contextId) {
    if (scopedIds.networkIds.includes(contextId)) return true;

    if (
      policy.managedNetworkIds.includes(contextId) &&
      scopedIds.networkIds.length === 0 &&
      scopedIds.organizationIds.length > 0
    ) {
      return true;
    }

    return false;
  }

  if (context?.type === 'consultant' && policy.consultantIds.length > 0) {
    return (
      hasIntersection(scopedIds.consultantIds, policy.consultantIds) ||
      hasIntersection(scopedIds.networkIds, policy.managedNetworkIds) ||
      hasIntersection(scopedIds.organizationIds, policy.managedOrganizationIds)
    );
  }

  return (
    hasIntersection(scopedIds.organizationIds, policy.managedOrganizationIds) ||
    hasIntersection(scopedIds.networkIds, policy.managedNetworkIds) ||
    hasIntersection(scopedIds.consultantIds, policy.consultantIds)
  );
};

export const filterUsersForPolicy = (users = [], currentUser, context = { type: 'hke' }) => {
  if (!Array.isArray(users)) return [];

  const policy = getUserCreationPolicy(currentUser);
  if (policy.isHkeAdmin) return users;

  return users.filter((targetUser) => canAccessUserInContext(currentUser, targetUser, context));
};
